import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'
import { logoutOneSignalUser } from '../config/onesignal'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)
  const [logoutSessionCreated, setLogoutSessionCreated] = useState(false)

  useEffect(() => {
    // Check localStorage first for custom login
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        setUser(userData)
        setLoading(false)
        return
      } catch (error) {
        console.error('Error parsing stored user:', error)
        localStorage.removeItem('user')
      }
    }

    // Get initial session from Supabase
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session)
      
      // Check localStorage first for custom login
      const storedUser = localStorage.getItem('user')
      
      if (session?.user) {
        setUser(session.user)
      } else if (event === 'SIGNED_OUT') {
        // Only clear user on explicit sign out
        setUser(null)
        localStorage.removeItem('user')
      } else if (storedUser && !session) {
        // If we have stored user but no Supabase session, keep the stored user
        try {
          const userData = JSON.parse(storedUser)
          setUser(userData)
          console.log('🔄 Keeping stored user from localStorage')
        } catch (error) {
          console.error('Error parsing stored user:', error)
          localStorage.removeItem('user')
        }
      }
      
      setLoading(false)
      
      // Create session record for Supabase auth events
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('🔐 SIGNED_IN event detected, creating session...')
        setLogoutSessionCreated(false) // Reset logout flag for new login
        await createSession(session.user, 'login')
      } else if (event === 'SIGNED_OUT' && user && !logoutSessionCreated) {
        console.log('🚪 SIGNED_OUT event detected, creating logout session...')
        // Create logout session record
        try {
          console.log('📝 Creating logout session for user:', user.email)
          setLogoutSessionCreated(true)
          await createSession(user, 'logout')
          console.log('✅ Logout session created successfully')
        } catch (error) {
          console.error('❌ Error creating logout session:', error)
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Additional useEffect to periodically check localStorage and restore user if needed
  useEffect(() => {
    const checkStoredUser = () => {
      const storedUser = localStorage.getItem('user')
      if (storedUser && !user) {
        try {
          const userData = JSON.parse(storedUser)
          setUser(userData)
          console.log('🔄 Restored user from localStorage')
        } catch (error) {
          console.error('Error parsing stored user:', error)
          localStorage.removeItem('user')
        }
      }
    }

    // Check immediately
    checkStoredUser()

    // Check every 2 seconds to catch any missed updates
    const interval = setInterval(checkStoredUser, 2000)

    return () => clearInterval(interval)
  }, [user])

  const signUp = async (email, password, userData) => {
    try {
      setLoading(true)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      })
      
      if (error) throw error
      
      toast.success('Account created successfully! Please check your email to verify your account.')
      return { data, error: null }
    } catch (error) {
      toast.error(error.message)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    try {
      setLoading(true)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) throw error
      
      // Session will be created by onAuthStateChange SIGNED_IN event
      
      toast.success('Welcome back!')
      return { data, error: null }
    } catch (error) {
      toast.error(error.message)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      
      // Create logout session record before signing out
      if (user && !logoutSessionCreated) {
        try {
          console.log('🚪 Creating logout session before sign out')
          setLogoutSessionCreated(true)
          await createSession(user, 'logout')
          console.log('✅ Logout session created successfully')
        } catch (error) {
          console.error('❌ Error creating logout session:', error)
        }
      }
      
      logoutOneSignalUser()
      const { error } = await supabase.auth.signOut()
      if (error) throw error

      toast.success('Signed out successfully')
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email) => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      })
      
      if (error) throw error
      
      toast.success('Password reset email sent!')
      return { error: null }
    } catch (error) {
      toast.error(error.message)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  // Function to get user's browser and device info
  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent
    
    // Get actual screen dimensions from browser
    const screenWidth = window.screen.width
    const screenHeight = window.screen.height
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    
    // Simple device type detection based on screen size
    let deviceType = 'web' // default
    if (screenWidth <= 768) {
      deviceType = 'mobile'
    } else if (screenWidth > 768 && screenWidth <= 1024) {
      deviceType = 'tablet'
    }
    
    // Extract browser name from user agent (don't hardcode)
    let browser = ''
    if (userAgent.includes('Chrome')) browser = 'Chrome'
    else if (userAgent.includes('Firefox')) browser = 'Firefox'
    else if (userAgent.includes('Safari')) browser = 'Safari'
    else if (userAgent.includes('Edge')) browser = 'Edge'
    else browser = '' // Leave blank if not detected
    
    // Extract OS from user agent (don't hardcode)
    // Check mobile OS first, then desktop OS (Android user agents also contain "Linux")
    let os = ''
    if (userAgent.includes('Android')) os = 'Android'
    else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS'
    else if (userAgent.includes('Windows')) os = 'Windows'
    else if (userAgent.includes('Mac')) os = 'Mac'
    else if (userAgent.includes('Linux')) os = 'Linux'
    else os = '' // Leave blank if not detected
    
    const deviceInfo = {
      user_agent: userAgent,
      device_type: deviceType,
      browser,
      os
    }
    
    return deviceInfo
  }

  // Function to get user's IP address
  const getUserIP = async () => {
    try {
      console.log('🌐 Starting IP fetch from ipify.org...')
      const response = await fetch('https://api.ipify.org?format=json')
      console.log('🌐 IP response status:', response.status)
      const data = await response.json()
      console.log('🌐 IP response data:', data)
      return data.ip
    } catch (error) {
      console.error('❌ Could not fetch IP address:', error)
      return null
    }
  }

  // Function to get location from IP (using a free service)
  const getLocationFromIP = async (ip) => {
    if (!ip) {
      return null
    }
    try {
      const response = await fetch(`https://ipapi.co/${ip}/json/`)
      const data = await response.json()
      
      const locationData = {
        city: data.city || null,
        state: data.region || null,
        country: data.country_name || null,
        latitude: data.latitude || null,
        longitude: data.longitude || null
      }
      
      return locationData
    } catch (error) {
      console.error('❌ Could not fetch location:', error)
      return null
    }
  }

  // Function to create session record
  const createSession = async (userData, eventType = 'login') => {
    try {
      const deviceInfo = getDeviceInfo()
      const sessionToken = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      // Get real IP and location data with timeout
      const userIP = await Promise.race([
        getUserIP(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('IP fetch timeout')), 5000))
      ]).catch(error => {
        console.warn('IP fetch failed or timed out:', error.message)
        return null
      })
      
      const locationData = await Promise.race([
        getLocationFromIP(userIP),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Location fetch timeout')), 5000))
      ]).catch(error => {
        console.warn('Location fetch failed or timed out:', error.message)
        return null
      })
      
      // Determine user type based on login context/page and user role
      // Only 2 types: 'employee' (admin dashboard) and 'customer' (customer dashboard)
      let userType = 'customer' // default for customer dashboard
      
      // Check if user is logging in from admin dashboard
      const currentPath = window.location.pathname
      const isAdminLogin = currentPath.includes('/admin') || currentPath.includes('/dashboard')
      
      // Also check user role for admin users
      const isAdminRole = userData.role === 'admin' || userData.role === 'super_admin' || 
                         userData.role === 'SUPER_ADMIN' || userData.role === 'manager' ||
                         userData.role === 'employee' || userData.role === 'support'
      
      if (isAdminLogin || isAdminRole) {
        userType = 'employee' // Admin dashboard login OR admin role = employee
      }
      // Customer users don't need permissions - they have basic access only
      
      const sessionData = {
        user_id: userData.id || userData.user_id,
        user_type: userType,
        email: userData.email,
        session_token: sessionToken,
        ip_address: userIP,
        user_agent: deviceInfo.user_agent,
        device_type: deviceInfo.device_type,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        location_city: locationData?.city || null,
        location_state: locationData?.state || null,
        location_country: locationData?.country || null,
        location_latitude: locationData?.latitude || null,
        location_longitude: locationData?.longitude || null
      }

      if (eventType === 'logout') {
        sessionData.logout_time = new Date().toISOString()
        sessionData.logout_reason = 'user_logout'
        sessionData.session_type = 'logout'
      } else {
        sessionData.login_time = new Date().toISOString()
        sessionData.session_type = 'login'
      }

      console.log('📝 Session data to insert:', sessionData)

      const { data, error } = await supabase
        .from('sessions')
        .insert([sessionData])
        .select()

      if (error) {
        console.error('❌ Error creating session:', error)
        return null
      }

      console.log('✅ Session created successfully:', data[0])
      return data[0]
    } catch (error) {
      console.error('❌ Error in createSession:', error)
      return null
    }
  }


  const login = async (userData) => {
    console.log('🔐 login function called with:', userData)
    setUser(userData)
    setLoading(false)
    
    // Session will be created by onAuthStateChange SIGNED_IN event
  }

  const logout = async () => {
    // Create logout session record if user is logged in
    if (user && !logoutSessionCreated) {
      console.log('🚪 Manual logout - creating logout session for user:', user.email)
      
      try {
        // Create a logout session record directly
        setLogoutSessionCreated(true)
        await createSession(user, 'logout')
        console.log('✅ Logout session created successfully')
      } catch (error) {
        console.error('❌ Error creating logout session:', error)
      }
    }

    logoutOneSignalUser()

    localStorage.removeItem('user')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('userType')

    setUser(null)
    setSession(null)
    setLoading(false)
  }

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    login,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
